import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatNativeDateModule} from '@angular/material/core';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {Router} from '@angular/router';
import {AppointmentsController} from '../appointments/service/appointments.controller';
import {AppointmentsFlowStateService} from '../../common/service/appointments-flow-state.service';
import {
  AppointmentAvailability,
  AppointmentDayAvailability,
  AppointmentSlot
} from '../appointments/model/appointments-models';
import {ErrorBannerComponent} from '../appointments-shared/error-banner/error-banner.component';

@Component({
  selector: 'app-appointments-calendar',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    ErrorBannerComponent
  ],
  templateUrl: './appointments-calendar.component.html',
  styleUrls: ['./appointments-calendar.component.scss']
})
export class AppointmentsCalendarComponent implements OnInit {
  isLoading = true;
  errorMessage: string | null = null;

  availability: AppointmentAvailability | null = null;
  selectedDay: AppointmentDayAvailability | null = null;
  selectedSlot: AppointmentSlot | null = null;
  selectedDate: Date | null = null;

  constructor(
    private controller: AppointmentsController,
    private flowState: AppointmentsFlowStateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const preselection = this.flowState.snapshot.preselection;
    this.selectedDay = this.flowState.snapshot.selectedDay;
    this.selectedSlot = this.flowState.snapshot.selectedSlot;
    if (this.selectedDay) {
      this.selectedDate = new Date(this.selectedDay.date);
    }

    this.controller.loadAvailability(preselection).subscribe({
      next: (availability) => {
        this.availability = availability;
        this.isLoading = false;

        if (this.selectedDay) {
          const refreshedDay = availability.days.find(day => day.date === this.selectedDay?.date);
          if (refreshedDay) {
            this.selectedDay = refreshedDay;
            this.selectedDate = new Date(refreshedDay.date);
            if (this.selectedSlot) {
              const refreshedSlot = refreshedDay.slots.find(slot => slot.id === this.selectedSlot?.id);
              this.selectedSlot = refreshedSlot ?? null;
            }
          }
        }

        if (!this.selectedDay && availability.days.length) {
          this.selectDay(availability.days[0]);
        }
      },
      error: () => {
        this.errorMessage = 'Die Verfügbarkeiten konnten nicht geladen werden. Bitte versuchen Sie es erneut.';
        this.isLoading = false;
      }
    });
  }

  onDateSelected(date: Date | null) {
    if (!date || !this.availability) return;
    const iso = this.toIsoDate(date);
    const day = this.availability.days.find(d => d.date === iso) ?? {
      date: iso,
      displayDate: date.toLocaleDateString('de-DE', {weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'}),
      slots: []
    };
    this.selectedDate = date;
    this.selectDay(day);
  }

  selectDay(day: AppointmentDayAvailability) {
    this.selectedDay = day;
    this.selectedSlot = null;
    this.flowState.updateSelectedDay(day);
  }

  selectSlot(slot: AppointmentSlot) {
    this.selectedSlot = slot;
    if (this.selectedDay) {
      this.flowState.updateSelectedSlot(this.selectedDay, slot);
    }
  }

  get hasSlotSelection(): boolean {
    return !!this.selectedSlot && !!this.selectedDay;
  }

  continue() {
    if (!this.selectedDay || !this.selectedSlot) return;
    this.router.navigate(['/patient']);
  }

  back() {
    this.router.navigate(['/vorauswahl']);
  }

  dateClass = (date: Date, view: 'month' | 'year' | 'multi-year'): string => {
    if (view !== 'month' || !this.availability) {
      return '';
    }
    const iso = this.toIsoDate(date);
    const day = this.availability.days.find(d => d.date === iso);
    if (!day) {
      return '';
    }
    return day.slots.length > 0 ? 'has-slots' : 'no-slots';
  };

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
